// Light-weight relative time formatter. The Topbar's clock-icon tooltips
// and the "update detected ago" badge both want short copy like "2h ago" /
// "há 3d" / "hace 5 sem", so a small helper hand-rolled against the
// i18n key set we already maintain beats pulling in date-fns.
type TFunc = (key: string, opts?: Record<string, unknown>) => string

export function relativeTime(isoOrDate: string | Date, t: TFunc): string {
  return relativeTimeShort(isoOrDate, t)
}

/** Seconds elapsed, floored at zero so server clock skew can't make a stamp
 *  land "in the future" on screen. */
function secondsSince(then: Date): number {
  return Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000))
}

/**
 * The long variant is the account's session list, where rows are few and the
 * sentence form reads better than clock shorthand. It shares the floor/now
 * helpers with the short form so the two can never disagree about what
 * "recent" means.
 */
export function relativeTimeLong(isoOrDate: string | Date, t: TFunc): string {
  const then = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate
  if (Number.isNaN(then.getTime())) return ''
  const sec = secondsSince(then)
  const min = Math.floor(sec / 60)
  if (min < 1) return t('account.session_now', { defaultValue: 'active now' })
  if (min < 60) return t('account.session_min_ago', { count: min, defaultValue: '{{count}} min ago' })
  const hr = Math.floor(min / 60)
  if (hr < 24) return t('account.session_hours_ago', { count: hr, defaultValue: '{{count}} h ago' })
  const day = Math.floor(hr / 24)
  if (day < 31) return t('account.session_days_ago', { count: day, defaultValue: '{{count}} d ago' })
  return formatLocalYMD(then)
}

function relativeTimeShort(isoOrDate: string | Date, t: TFunc): string {
  const then = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate
  if (Number.isNaN(then.getTime())) return ''
  const sec = secondsSince(then)
  if (sec < 45) return t('common.time_now', { defaultValue: 'now' })
  const min = Math.floor(sec / 60)
  if (min < 60) return t('common.time_min_ago', { count: min, defaultValue: '{{count}}m ago' })
  const hr = Math.floor(min / 60)
  if (hr < 24) return t('common.time_hr_ago', { count: hr, defaultValue: '{{count}}h ago' })
  const day = Math.floor(hr / 24)
  if (day < 7) return t('common.time_day_ago', { count: day, defaultValue: '{{count}}d ago' })
  const wk = Math.floor(day / 7)
  if (wk < 5) return t('common.time_wk_ago', { count: wk, defaultValue: '{{count}}w ago' })
  // For older dates just show the date itself — relative time loses meaning
  // past a few weeks and the user usually wants the absolute marker anyway.
  // Local civil date (not UTC) so evening stamps don't flip the calendar day.
  return formatLocalYMD(then)
}

export function formatLocalYMD(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export type CheckInterval = 'hourly' | 'daily' | 'weekly'

const intervalMs: Record<CheckInterval, number> = {
  hourly: 60 * 60 * 1000,
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
}

// Forecast when the next changecheck scan will pick the link up. Mirrors
// `FindDueForCheck` in backend/internal/links/repository.go: due iff
// `last_checked_at IS NULL OR last_checked_at < now() - interval`. The
// backend predicate is strict `<`, so a link is due at `last + interval`.
// We round to a 60 s "soon" bucket to absorb the scan tick (also default
// 60 s) — telling the user "in 30 s" is noisier than truthful given the
// scheduler's resolution.
//
// `link` is the dialog's snapshot; PATCH bumps `last_checked_at` on the
// backend but the dialog closes on save, so the preview is always sourced
// from the snapshot the user opened.
export function nextCheckPreview(
  interval: CheckInterval | null | undefined,
  lastCheckedAt: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
  now: Date = new Date(),
): string {
  if (!interval) return ''
  // Defensive guard: TS callers can't reach this, but a stale call site
  // passing a string outside the union would otherwise produce "in NaNd".
  const step = intervalMs[interval]
  if (!step) return ''
  if (!lastCheckedAt) return t('common.next_check_soon', { defaultValue: 'soon (within a minute)' })
  const last = new Date(lastCheckedAt)
  if (Number.isNaN(last.getTime())) return t('common.next_check_soon', { defaultValue: 'soon (within a minute)' })
  const nextMs = last.getTime() + step
  const remaining = nextMs - now.getTime()
  if (remaining <= 60_000) return t('common.next_check_soon', { defaultValue: 'soon (within a minute)' })
  const min = Math.floor(remaining / 60_000)
  if (min < 60) return t('common.next_check_in_min', { count: min, defaultValue: 'in {{count}}m' })
  const hr = Math.floor(min / 60)
  if (hr < 24) return t('common.next_check_in_hr', { count: hr, defaultValue: 'in {{count}}h' })
  const day = Math.floor(hr / 24)
  // Clock skew / hostile timestamp could land last_checked_at in the
  // far future and produce "in 3650000d". Past a year we fall back to
  // a local Y-M-D stamp — same shape `relativeTime` uses for ancient values.
  if (day > 365) return formatLocalYMD(new Date(nextMs))
  return t('common.next_check_in_day', { count: day, defaultValue: 'in {{count}}d' })
}
